import React, { useState, useCallback } from 'react';
import { Lock, Zap, ArrowRight, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getSystemStatus } from '../utils/api';

interface AdminLoginProps {
  onLogin: (secret: string) => void;
}

export const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = secret.trim();
      if (!trimmed) {
        setError('Enter admin secret');
        return;
      }
      setIsLoading(true);
      setError('');
      try {
        await getSystemStatus(trimmed);
        sessionStorage.setItem('botilia_admin_secret', trimmed);
        onLogin(trimmed);
      } catch {
        setError('Invalid admin secret');
      } finally {
        setIsLoading(false);
      }
    },
    [secret, onLogin],
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Brand */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Zap className="h-6 w-6 text-primary-foreground" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-bold tracking-tight">Botilia Admin</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Search Engine Control Panel</p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Sign In
            </CardTitle>
            <CardDescription>Enter your admin secret to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-secret">Admin Secret</Label>
                <Input
                  id="admin-secret"
                  type="password"
                  value={secret}
                  onChange={(e) => {
                    setSecret(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter secret..."
                  autoFocus
                  className={error ? 'border-destructive' : ''}
                />
                {error && (
                  <div className="flex items-center gap-1.5 text-destructive">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="text-xs">{error}</span>
                  </div>
                )}
              </div>

              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Checking...</>
                ) : (
                  <>Sign In <ArrowRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-muted-foreground/50 text-xs">
          Botilia.gr AI Search &middot; Admin Panel
        </p>
      </div>
    </div>
  );
};
